import React, { useEffect, useState } from 'react';
import { fetchSupplierActionItems, sortByUrgency, waitingLabel, ACTION_BUCKETS } from '../../lib/supplierActionItems';
import TranslatedText from '../TranslatedText';
import { unitLabel } from '../../lib/supplierRequestTerms';

// Unified supplier Requests tab (decision #1): everything that needs the supplier's
// response in ONE place, classified by STATUS (chips) — not by object type. Each
// card carries a small KIND label and a kind-specific action. Presentation lives
// here; the action routes back to the owner via onAction(item).

const T = {
  ar: {
    title: 'الطلبات الموجّهة إليك', sub: 'كل ما يحتاج تصرّفاً منك، بمكان واحد',
    buckets: { needs_response: 'يحتاج ردّك', my_offers: 'عروضي', accepted: 'مقبولة', finished: 'منتهية' },
    kinds: { rfq: 'طلب عرض سعر', direct: 'شراء مباشر', sample: 'طلب عيّنة', inquiry: 'استفسار على منتج', message: 'رسالة' },
    actions: { submit_offer: 'قدّم عرضك ←', confirm_order: 'أكّد الطلب ←', approve_sample: 'وافق أو اعتذر ←', reply_inquiry: 'ردّ على الاستفسار ←', view_offer: 'عرض العرض ←', view_order: 'عرض الطلب ←', view_sample: 'عرض العيّنة ←', view_inquiry: 'عرض الاستفسار ←', reply_message: 'ردّ على الرسالة ←', view_message: 'عرض المحادثة ←' },
    trader: 'تاجر', qty: 'الكمية', loading: 'جارٍ التحميل…',
    empty: 'لم يصلك طلب بعد',
    emptyBody: 'عندما يصل طلب يطابق تخصصك، يوجّهه فريقنا إليك هنا.',
    boostTitle: 'ارفع أولويتك في استقبال الطلبات:',
    boost: [
      { tab: 'verification', text: 'أكمل ملف شركتك — الموردون الموثّقون يحصلون على الأولوية في التوجيه' },
      { tab: 'settings',     text: 'حدّد فئاتك بدقة — كلما كان تخصصك أوضح، وصلتك طلبات أكثر' },
      { tab: 'add-product',  text: 'أضف منتجاتك — المصانع التي تعرض منتجاتها تتصدّر قائمة التوجيه، ويصلها التجار مباشرة من البحث' },
    ],
    routedTitle: 'الطلبات الموجّهة إليك',
    routedSub: 'اخترنا مصنعك لهذه الطلبات بناءً على تخصصك.',
    boostInline: 'تريد طلبات أكثر؟ أكمل ملفك وأضف منتجاتك لترفع أولويتك في التوجيه.',
  },
  en: {
    title: 'Requests for you', sub: 'Everything that needs your response, in one place',
    buckets: { needs_response: 'Needs your response', my_offers: 'My offers', accepted: 'Accepted', finished: 'Finished' },
    kinds: { rfq: 'Quote request', direct: 'Direct purchase', sample: 'Sample request', inquiry: 'Product inquiry', message: 'Message' },
    actions: { submit_offer: 'Submit your offer →', confirm_order: 'Confirm order →', approve_sample: 'Approve or decline →', reply_inquiry: 'Reply to inquiry →', view_offer: 'View offer →', view_order: 'View order →', view_sample: 'View sample →', view_inquiry: 'View inquiry →', reply_message: 'Reply →', view_message: 'View chat →' },
    trader: 'Trader', qty: 'Qty', loading: 'Loading…',
    empty: 'No requests yet',
    emptyBody: 'When a request matches your speciality, our team routes it to you here.',
    boostTitle: 'Get higher priority on incoming requests:',
    boost: [
      { tab: 'verification', text: 'Complete your company file — verified suppliers get priority in routing' },
      // Speciality is ONE field and it lives in Settings. No categories
      // section was created for it: a whole section for a single field before
      // launch is complexity with no return (owner, 2026-09-19).
      { tab: 'settings',     text: 'Set your categories precisely — the clearer your speciality, the more requests reach you' },
      { tab: 'add-product',  text: 'Add your products — factories with a live catalogue rank first in routing, and traders find them directly in search' },
    ],
    routedTitle: 'Routed to you',
    routedSub: 'We picked your factory for these, based on your speciality.',
    boostInline: 'Want more requests? Complete your file and add products to raise your routing priority.',
  },
  zh: {
    title: '为您推送的需求', sub: '所有需要您回应的事项，集中一处',
    buckets: { needs_response: '待您回应', my_offers: '我的报价', accepted: '已接受', finished: '已完成' },
    kinds: { rfq: '询价', direct: '直接采购', sample: '样品申请', inquiry: '产品咨询', message: '消息' },
    actions: { submit_offer: '提交报价 →', confirm_order: '确认订单 →', approve_sample: '批准或拒绝 →', reply_inquiry: '回复咨询 →', view_offer: '查看报价 →', view_order: '查看订单 →', view_sample: '查看样品 →', view_inquiry: '查看咨询 →', reply_message: '回复 →', view_message: '查看对话 →' },
    trader: '采购商', qty: '数量', loading: '加载中…',
    empty: '暂时还没有需求',
    emptyBody: '当有需求与您的专长匹配时，我们的团队会在这里推送给您。',
    boostTitle: '提升您获得需求推送的优先级：',
    boost: [
      { tab: 'verification', text: '完善公司资料 —— 已认证的供应商在推送中优先' },
      { tab: 'settings',     text: '准确设置您的品类 —— 专长越清晰，收到的需求越多' },
      { tab: 'add-product',  text: '上传您的产品 —— 有在线产品目录的工厂在推送中排名靠前，采购商也能直接搜索到' },
    ],
    routedTitle: '为您推送的需求',
    routedSub: '我们根据您的专长，为这些需求选择了您的工厂。',
    boostInline: '想获得更多需求？完善资料并上传产品，提升推送优先级。',
  },
};

export default function SupplierRequestsPanel({ sb, supplierId, lang = 'ar', onAction, onGoTab }) {
  const c = T[lang] || T.ar;
  const isAr = lang === 'ar';
  const arFont = isAr ? { fontFamily: 'var(--font-ar)' } : {};
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [bucket, setBucket] = useState('needs_response');
  const [names, setNames] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const { items: it, counts: ct } = await fetchSupplierActionItems(sb, supplierId, lang);
      if (!alive) return;
      setItems(it); setCounts(ct); setLoading(false);
      // Resolve counterparty names (supplier can read buyer profiles via the join
      // used elsewhere in the dashboard).
      const ids = [...new Set(it.map((x) => x.buyerId).filter(Boolean))];
      if (ids.length) {
        const { data } = await sb.from('profile_directory').select('id, full_name, company_name').in('id', ids);
        if (alive && data) setNames(data.reduce((a, p) => ({ ...a, [p.id]: p.company_name || p.full_name }), {}));
      }
    })();
    return () => { alive = false; };
  }, [sb, supplierId]);

  const inBucket = items.filter((x) => x.bucket === bucket);
  const shown = bucket === 'needs_response' ? sortByUrgency(inBucket) : inBucket;

  return (
    <div style={{ maxWidth: 640 }}>
      <h2 style={{ fontSize: isAr ? 24 : 30, fontWeight: 300, marginBottom: 4, color: 'var(--text-primary)', ...arFont }}>{c.title}</h2>
      <p style={{ fontSize: 13, color: 'var(--text-disabled)', marginBottom: 12, ...arFont }}>{c.sub}</p>
      {/* status chips */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', marginBottom: 18, paddingBottom: 2 }}>
        {ACTION_BUCKETS.map((b) => (
          <button key={b} onClick={() => setBucket(b)}
            style={{ padding: '7px 15px', borderRadius: 'var(--radius-pill)', border: '1px solid', whiteSpace: 'nowrap', cursor: 'pointer', fontSize: 13, minHeight: 34, ...arFont,
              background: bucket === b ? 'var(--text-primary)' : 'var(--bg-raised)',
              color: bucket === b ? 'var(--bg-base, #fff)' : 'var(--text-disabled)',
              borderColor: bucket === b ? 'var(--text-primary)' : 'var(--border-subtle)',
              fontWeight: bucket === b ? 600 : 400 }}>
            {c.buckets[b]}{counts[b] ? ` · ${counts[b]}` : ''}
          </button>
        ))}
      </div>

      {loading ? (
        <p style={{ color: 'var(--text-disabled)', fontSize: 14, ...arFont }}>{c.loading}</p>
      ) : shown.length === 0 ? (
        /* Emptiness must read as "nothing routed yet", never "nothing exists".
           There is no browse screen to go and look at: since 2026-10-11 every
           open direct request lands in this list on its own (source
           'open_pool', 20261011224335), and anything else arrives only when
           routed. So a bare "Nothing here right now" would leave a supplier
           thinking the platform is dead rather than that nothing is open. */
        <div style={{ padding: '44px 20px', maxWidth: 460, margin: '0 auto' }}>
          <p style={{ color: 'var(--text-primary)', fontSize: 17, marginBottom: 8, textAlign: 'center', ...arFont }}>{c.empty}</p>
          <p style={{ color: 'var(--text-disabled)', fontSize: 13, lineHeight: 1.8, margin: '0 0 28px', textAlign: 'center', ...arFont }}>{c.emptyBody}</p>

          {/* A checklist, not a nag banner: calm, secondary to the heading, no
              red and no "!" icons. The supplier has done nothing wrong — they
              are simply waiting — and the three items are the only levers they
              actually control over their routing priority. */}
          <p style={{ fontSize: 12, letterSpacing: 0.4, color: 'var(--text-secondary)', margin: '0 0 10px', ...arFont }}>{c.boostTitle}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {c.boost.map((b) => (
              <button
                key={b.tab}
                onClick={() => onGoTab?.(b.tab)}
                style={{
                  display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%',
                  textAlign: isAr ? 'right' : 'left', flexDirection: isAr ? 'row-reverse' : 'row',
                  background: 'none', border: 'none', borderBottom: '1px solid var(--border-subtle)',
                  padding: '12px 2px', cursor: 'pointer', ...arFont,
                }}
              >
                <span aria-hidden="true" style={{ color: 'var(--text-disabled)', fontSize: 13, lineHeight: 1.7 }}>—</span>
                <span style={{ flex: 1, fontSize: 13, lineHeight: 1.7, color: 'var(--text-secondary)' }}>{b.text}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <>
          {/* A routed supplier will often see ONE request. That must read as
              selection, not scarcity — they were picked, not left with the
              scraps. Shown only on the needs-your-response bucket, which is
              the one that holds routed work. */}
          {bucket === 'needs_response' && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 2px', ...arFont }}>{c.routedTitle}</p>
              <p style={{ fontSize: 12, color: 'var(--text-disabled)', margin: 0, lineHeight: 1.7, ...arFont }}>{c.routedSub}</p>
            </div>
          )}
          {shown.map((it) => {
        const urgent = it.bucket === 'needs_response';
        return (
          <button key={it.key} type="button" onClick={() => onAction?.(it)}
            style={{ display: 'block', width: '100%', textAlign: isAr ? 'right' : 'left', background: 'var(--bg-raised)', border: '1px solid var(--border-subtle)',
              [isAr ? 'borderRight' : 'borderLeft']: urgent ? '3px solid var(--bronze, #8B7355)' : undefined,
              borderRadius: 'var(--radius-lg)', padding: '15px 16px', marginBottom: 10, cursor: 'pointer' }}>
            <span style={{ fontSize: 10.5, letterSpacing: 1.4, textTransform: 'uppercase', color: 'var(--bronze, #8B7355)', fontWeight: 600, display: 'block', marginBottom: 5, ...arFont }}>{c.kinds[it.kind]}</span>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1.45, ...arFont }}>
                {it.title ? <TranslatedText text={it.title} lang={lang} /> : (names[it.buyerId] || c.trader)}
                {/* ON THE ROW, not behind a click. The supplier used to open the
                    offer to discover the request was over. Same red and same
                    wording as the badge in My Offers and the trader's list, so
                    the three surfaces read as one fact. */}
                {it.requestCancelled && (
                  <span style={{
                    marginInlineStart: 8, padding: '2px 9px', borderRadius: 'var(--radius-pill)',
                    border: '1px solid rgba(224,92,92,0.3)', color: 'var(--red)',
                    fontSize: 10, fontWeight: 600, whiteSpace: 'nowrap', ...arFont,
                  }}>
                    {isAr ? 'ملغي' : lang === 'zh' ? '已取消' : 'Cancelled'}
                  </span>
                )}
              </h3>
              <span style={{ fontSize: 11.5, color: urgent ? 'var(--bronze, #8B7355)' : 'var(--text-disabled)', whiteSpace: 'nowrap', fontWeight: urgent ? 600 : 400, ...arFont }}>
                {urgent ? waitingLabel(it.waitingSince, lang) : ''}
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: 'var(--text-disabled)', marginTop: 6, display: 'flex', gap: 14, flexWrap: 'wrap', ...arFont }}>
              {names[it.buyerId] && <span>{names[it.buyerId]}</span>}
              {/* The unit's label in the reader's language, not the raw key
                  ('carton') — same lookup every supplier door uses. */}
              {it.qty ? <span>{c.qty} {it.qty}{unitLabel(it.unit, lang) ? ` ${unitLabel(it.unit, lang)}` : ''}</span> : null}
            </div>
            {/* No call to action on a cancelled request -- there is nothing to
                go and do, and an arrow that invites the click is what sent
                suppliers into a dead request in the first place. The row stays
                (their quote is still a record) and the badge above says why. */}
            <div style={{ marginTop: 12, paddingTop: 11, borderTop: '1px solid var(--border-subtle)', fontSize: 13, fontWeight: 600, color: it.requestCancelled ? 'var(--text-disabled)' : 'var(--bronze, #8B7355)', ...arFont }}>
              {it.requestCancelled
                ? (isAr ? 'انتهى هذا الطلب' : lang === 'zh' ? '此需求已结束' : 'This request is over')
                : (c.actions[it.action] || '')}
            </div>
            </button>
          );
        })}

          {/* C: the ask stays visible BELOW the list, not only when empty. A
              supplier who has just received their first routed request is the
              most motivated they will ever be — that is the moment to ask for
              more, not to go quiet. One line, lighter than the empty-state
              checklist. */}
          <button
            onClick={() => onGoTab?.('verification')}
            style={{
              display: 'block', width: '100%', marginTop: 6, padding: '12px 2px',
              background: 'none', border: 'none', borderTop: '1px solid var(--border-subtle)',
              textAlign: isAr ? 'right' : 'left', cursor: 'pointer',
              fontSize: 12.5, lineHeight: 1.7, color: 'var(--text-disabled)', ...arFont,
            }}
          >
            {c.boostInline}
          </button>
        </>
      )}
    </div>
  );
}
